import MediaFrame from "@/components/MediaFrame"
import { useAuth } from "@/hooks/useAuth"
import { CITY_KEY, TOPICS, isBoosted } from "@/lib/culture"
import { isLivePost, isSeedPost, postPath, toPublicPost } from "@/lib/filters"
import { lumi } from "@/lib/lumi"
import { listBlockedIds } from "@/lib/social"
import type { Post } from "@/types"
import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"

export default function ExplorePage() {
  const { user } = useAuth()
  const [query, setQuery] = useState("")
  const [chip, setChip] = useState("foryou")
  const [city, setCity] = useState("")
  const [askCity, setAskCity] = useState(false)
  const [posts, setPosts] = useState<Post[]>([])
  const [blockedIds, setBlockedIds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      lumi.entities.posts.list({ sort: { createdAt: -1 }, limit: 120 }),
      listBlockedIds(user?.userId),
    ])
      .then(([postRes, blocked]) => {
        setBlockedIds(blocked)
        setPosts(((postRes?.list || []) as Post[]).map((post) => toPublicPost(post, user?.userId)).filter((post) => isLivePost(post) && !isSeedPost(post)))
      })
      .catch((err) => console.error("Explore load failed:", err))
      .finally(() => setLoading(false))
  }, [user?.userId])

  useEffect(() => {
    const stored = localStorage.getItem(CITY_KEY)
    if (stored) setCity(stored)
  }, [])

  useEffect(() => {
    if (chip === "nearby" && !city) setAskCity(true)
  }, [chip, city])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const blocked = new Set(blockedIds)
    return posts
      .filter((post) => {
        if (blocked.has(post.authorId) || blocked.has(post.ownerId || "")) return false
        if (q && !(post.caption || "").toLowerCase().includes(q) && !(post.city || "").toLowerCase().includes(q) && !(post.hashtags || "").toLowerCase().includes(q)) return false
        if (chip === "nearby") return city ? (post.city || "").toLowerCase() === city.toLowerCase() : true
        if (chip !== "foryou") return (post.topic || "other") === chip
        return true
      })
      .sort((a, b) => Number(isBoosted(b.boostedUntil)) - Number(isBoosted(a.boostedUntil)))
  }, [posts, query, chip, city, blockedIds])

  return (
    <div className="px-3 py-4 sm:px-6 lg:px-8">
      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search captions, locations, tags"
        className="w-full rounded-full border border-[#1F1F1F] bg-[#0B0B0B] px-4 py-3 text-sm outline-none"
      />
      <div className="mt-3 flex gap-2 overflow-x-auto pb-2">
        {TOPICS.map((topic) => (
          <button
            key={topic.id}
            type="button"
            onClick={() => setChip(topic.id)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-xs ${chip === topic.id ? "bg-[#00FF66] font-semibold text-black" : "bg-[#141414] text-[#A8A8A8]"}`}
          >
            {topic.label}
          </button>
        ))}
      </div>
      {askCity ? (
        <div className="mt-2 rounded-2xl border border-[#1F1F1F] bg-[#0B0B0B] p-3">
          <p className="text-sm">What location should Nearby use?</p>
          <input value={city} onChange={(event) => setCity(event.target.value)} className="mt-2 w-full rounded-xl bg-black px-3 py-2 text-sm outline-none" placeholder="Location" />
          <button
            type="button"
            onClick={() => {
              localStorage.setItem(CITY_KEY, city)
              setAskCity(false)
            }}
            className="mt-2 rounded-xl bg-[#00FF66] px-3 py-2 text-xs font-semibold text-black"
          >
            Save location
          </button>
        </div>
      ) : null}
      {loading ? (
        <div className="mt-3 grid grid-cols-3 gap-1 sm:grid-cols-4 lg:grid-cols-6">
          {Array.from({ length: 9 }).map((_, index) => <div key={index} className="aspect-square animate-pulse bg-[#141414]" />)}
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-3 gap-1 sm:grid-cols-4 lg:grid-cols-6">
          {filtered.map((post) => (
            <Link key={post._id} to={postPath(post)} state={{ post }} className="relative aspect-square overflow-hidden bg-black transition-opacity active:opacity-80">
              <MediaFrame post={post} preview autoPlay={false} fill />
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
